import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { useClub } from '../../hooks/useClub';

function interpolateScript(body, memberName, repName, clubName) {
  if (!body) return body;
  return body
    .replace(/\[Name\]/g, (() => { const n = memberName?.split(' ')[0]; return n ? n.charAt(0).toUpperCase() + n.slice(1) : '[Name]'; })())
    .replace(/\[Rep Name\]/g, repName || '[Rep Name]')
    .replace(/\[Club Name\]/g, clubName || '[Club Name]');
}

// Win Back offer scripts (Sprint 21.3, 9/29/26) — exact copy from the
// "Win Back Offer Scoping — TX Clubs" doc, both confirmed under 160
// characters for SMS. Discount takes priority over the 90-day offer on
// overlap (enforced in queries.js — winBackOfferBadges never sets both).
const WIN_BACK_DISCOUNT_SCRIPT = "Hi [First Name], it's The Picklr [Club]! We miss you on the courts. Come back this month for 20% off — just reply YES and we'll set it up.";
const WIN_BACK_90DAY_SCRIPT = "Hi [First Name], it's The Picklr [Club]! Ready to get back on the courts? Come back now and buy 2 months, get 1 free. Reply YES to claim it!";

function fillWinBackScript(template, memberName, clubName) {
  const firstName = memberName?.split(' ')[0] || 'there';
  return template.replace(/\[First Name\]/g, firstName).replace(/\[Club\]/g, clubName || '[Club]');
}

export function WinBackOfferScript({ prospect, clubName }) {
  const [copied, setCopied] = useState(false);
  const isDiscount = prospect.discountEligible;
  const template = isDiscount ? WIN_BACK_DISCOUNT_SCRIPT : WIN_BACK_90DAY_SCRIPT;
  const text = fillWinBackScript(template, prospect.name, clubName);
  const label = isDiscount ? '🎾 Still visiting since cancelling — 20% one-month offer' : '💙 Inactive 90+ days — Buy 2 Get 1 offer';
  const color = isDiscount ? '#22c55e' : '#3b82f6';

  return (
    <div style={{ marginBottom: 14, background: `${color}11`, border: `1px solid ${color}44`, borderRadius: 6, padding: '9px 10px' }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.45, marginBottom: 6 }}>
        {text}
      </div>
      <button
        className="btn btn-ghost btn-sm"
        style={{ fontSize: 11 }}
        onClick={() => {
          navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          });
        }}
      >
        {copied ? '✓ Copied' : '📋 Copy script'}
      </button>
    </div>
  );
}

const OUTCOMES = [
  'Sold Trial',
  'Sold Membership',
  'Engaged',
  'Follow-Up Needed',
  'No Response',
  'Not Interested — Cool Period',
  'Not Interested — Do Not Contact',
  'Nurture',
  'Cool Period',
  'Do Not Contact',
  'Closed',
];

export const BASE_PLAN_TYPES = [
  'Unlimited Nationwide — Full Price',
  'Unlimited Nationwide — 10% Discount (Closer)',
  'Unlimited Club Only — 20% Discount (Closer)',
  'Social Membership',
  'Junior Membership',
  'Punch Card',
  'Current Promo',
  'Trial Offer',
];

export const DENTON_PLAN_TYPES = [
  'Unlimited Nationwide — Full Price',
  'Unlimited Nationwide — 10% Discount (Closer)',
  'Unlimited Club Only — 20% Discount (Closer)',
  'Social Membership',
  'Junior Membership',
  'Punch Card',
  'Summer Slam — $49/mo Daytime Club',
  'Summer Slam — $65/mo Social',
  'Summer Slam — $79/mo Adult Unlimited',
  'Current Promo',
  'Trial Offer',
];

export const OFFER_SEQUENCE_SHOWN_FOR = ['Sold Trial', 'Sold Membership', 'Engaged', 'Follow-Up Needed', 'No Response', 'Not Interested — Cool Period', 'Declined'];

// ─── Offer Sequence Panel (GAP-L4) ───────────────────────────────────────────
export function OfferSequencePanel({ offerSequence, onChange }) {
  const { offers, discountUsed, discountAmount } = offerSequence;

  function setOffer(i, field, value) {
    const newOffers = [...offers];
    newOffers[i] = { ...newOffers[i], [field]: value };
    // If offer not made, clear type
    if (field === 'made' && !value) newOffers[i].type = '';
    onChange({ ...offerSequence, offers: newOffers });
  }

  const jumpedToDiscount = discountUsed && offers[0]?.made && !offers[1]?.made;

  return (
    <div style={{
      background: 'var(--surface-2)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '12px 14px', marginBottom: 14,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        📊 Offer Sequence
      </div>

      {/* Offer rows */}
      {offers.map((offer, i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 50 }}>
              {i === 0 ? '1st Offer' : i === 1 ? '2nd Offer' : '3rd Offer'}
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={offer.made || false}
                onChange={e => setOffer(i, 'made', e.target.checked)}
              />
              Made
            </label>
            {offer.made && (
              <select
                className="field-select"
                value={offer.type || ''}
                onChange={e => setOffer(i, 'type', e.target.value)}
                style={{ fontSize: 12, padding: '3px 8px', flex: 1, minWidth: 120 }}
              >
                <option value="">Select type...</option>
                {OFFER_TYPES_FULL_BASE.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
            {!offer.made && i > 0 && offers[i-1]?.made === false && (
              <span style={{ fontSize: 10, color: '#555', fontStyle: 'italic' }}>— skipped</span>
            )}
          </div>
        </div>
      ))}

      {/* Promo + Term + Close Date */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={offerSequence.promoOffered || false}
            onChange={e => onChange({ ...offerSequence, promoOffered: e.target.checked })}
          />
          <span>Promo Presented</span>
        </label>

        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Term Offered</div>
          <select
            className="field-select"
            value={offerSequence.termOffered || ''}
            onChange={e => onChange({ ...offerSequence, termOffered: e.target.value })}
            style={{ fontSize: 12, padding: '4px 8px' }}
          >
            <option value="">Select term...</option>
            {TERM_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Close Date</div>
          <input
            className="field-input"
            type="date"
            value={offerSequence.closeDate || ''}
            onChange={e => onChange({ ...offerSequence, closeDate: e.target.value })}
            style={{ fontSize: 12, padding: '4px 8px', width: '100%' }}
          />
        </div>
      </div>

      {/* Discount section */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginBottom: 6 }}>
          <input
            type="checkbox"
            checked={discountUsed || false}
            onChange={e => onChange({ ...offerSequence, discountUsed: e.target.checked, discountAmount: e.target.checked ? discountAmount : '' })}
          />
          <span>Discount Used</span>
        </label>
        {discountUsed && (
          <input
            className="field-input"
            type="text"
            placeholder="Discount amount or description (e.g. $20 off, 10%)"
            value={discountAmount || ''}
            onChange={e => onChange({ ...offerSequence, discountAmount: e.target.value })}
            style={{ fontSize: 12, padding: '5px 8px', width: '100%' }}
          />
        )}
      </div>

      {/* Jumped to discount warning */}
      {jumpedToDiscount && (
        <div style={{
          marginTop: 8, padding: '6px 10px',
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 5, fontSize: 11, color: '#ef4444', fontWeight: 600,
        }}>
          ⚠ Discount used without working full offer sequence — review with manager
        </div>
      )}
    </div>
  );
}

export const CLOSE_RESULTS = [
  'Sold',
  'Declined',
  'Thinking About It',
  'No Response',
  'Follow-Up Needed',
];

const TERM_OPTIONS = [
  'Monthly',
  'Annual',
  'Both Presented',
];

export const TIER_SOLD_OPTIONS_BASE = [
  'Move to L2P Sheet',
  'Trial Offer',
  'Unlimited Nationwide — Full Price',
  'Unlimited Nationwide — 10% Discount (Closer)',
  'Unlimited Club Only — 20% Discount (Closer)',
  'Social Membership',
  'Junior Membership',
  'Punch Card',
  'Current Promo',
  'Offer Declined — Follow Up <30 Days',
  'Offer Declined — Cool 30 Days',
  'Offer Declined — Cool 60 Days',
  'Onboard to T2M Sheet',
  'Do Not Contact',
  'Closed',
];

export const TIER_SOLD_OPTIONS_DEN = [
  'Move to L2P Sheet',
  'Trial Offer',
  'Unlimited Nationwide — Full Price',
  'Unlimited Nationwide — 10% Discount (Closer)',
  'Unlimited Club Only — 20% Discount (Closer)',
  'Social Membership',
  'Junior Membership',
  'Punch Card',
  'Summer Slam — $49/mo Daytime Club',
  'Summer Slam — $65/mo Social',
  'Summer Slam — $79/mo Adult Unlimited',
  'Current Promo',
  'Offer Declined — Follow Up <30 Days',
  'Offer Declined — Cool 30 Days',
  'Offer Declined — Cool 60 Days',
  'Onboard to T2M Sheet',
  'Do Not Contact',
  'Closed',
];

const FOLLOWUP_OUTCOMES = [
  'Not Needed',
  'Sold',
  'Declined',
  'No Response',
  'Call Back',
  'Engaged',
];

const OFFER_TYPES_FULL_BASE = [
  'Trial Offer',
  'Unlimited Nationwide — Full Price',
  'Unlimited Nationwide — 10% Discount (Closer)',
  'Unlimited Club Only — 20% Discount (Closer)',
  'Social Membership',
  'Junior Membership',
  'Punch Card',
  'Current Promo',
];

const OFFER_TYPES_FULL_DEN = [
  'Trial Offer',
  'Unlimited Nationwide — Full Price',
  'Unlimited Nationwide — 10% Discount (Closer)',
  'Unlimited Club Only — 20% Discount (Closer)',
  'Social Membership',
  'Junior Membership',
  'Punch Card',
  'Summer Slam — $49/mo Daytime Club',
  'Summer Slam — $65/mo Social',
  'Summer Slam — $79/mo Adult Unlimited',
  'Current Promo',
];

const CONTACT_METHOD_WHY = {
  text: "A text is lower friction for someone who hasn't had recent personal contact with the club. Higher open rate, no pressure to respond immediately. If no response in 24–48 hours, escalate to a call.",
  call: "This pipeline signals a higher-value relationship. A call is more personal and appropriate — it shows you're treating them as a valued contact, not mass outreach. Text first can feel impersonal at this level.",
  email: "Email is the lowest-engagement channel for this outreach type. Use only after text and call have been attempted. Keep it brief with a single clear call to action.",
};

const PIPELINE_WHY = {
  lead_engine: "This member has recent engagement signals — they're in the active scoring window. Contact now while the signal is fresh.",
  lead_winback_nonmember: "This person was once engaged but hasn't returned. A text-first approach keeps it low pressure while re-opening the door.",
  lead_winback_pastmember: "This was a paying member. A call is appropriate — they had a real relationship with the club and deserve a personal touch, not a mass text.",
  lead_trial_ready: "High readiness signal detected. The window is 2–3 weeks before engagement drops off. Call now — timing is critical.",
};

function scriptKeyForPipeline(pipeline) {
  if (!pipeline) return 'lead_winback_nonmember';
  const p = pipeline.toLowerCase();
  if (p.includes('past member') || p.includes('past_member')) return 'lead_winback_pastmember';
  if (p.includes('trial ready') || p.includes('trial_ready'))  return 'lead_trial_ready';
  return 'lead_winback_nonmember';
}

function pipelineKey(pipeline) {
  if (!pipeline) return 'lead_engine';
  const p = pipeline.toLowerCase().replace(/\s+/g, '_');
  return p;
}

// ─── Script Panel ─────────────────────────────────────────────────────────────
function ScriptPanel({ pipeline, clubId, memberName, repName, clubName }) {
  const { get } = useApi();
  const [script, setScript]       = useState(null);
  const [allScripts, setAllScripts] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading]     = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');
  const [copied, setCopied]       = useState(false);
  const [whyOpen, setWhyOpen]     = useState({});

  useEffect(() => {
    if (!pipeline) { setLoading(false); return; }
    const scriptKey = scriptKeyForPipeline(pipeline);
    // GAP-fix (Sprint 19): was fetching /scripts with no module filter, relying
    // on script_key matching alone to find the right one — a real risk if any
    // other module's script ever shares a key. Now scoped to module=lead,
    // matching the pattern already used by PB101.
    get('/scripts?module=lead_engine')
      .then(data => {
        const scripts = data.scripts || [];
        setAllScripts(scripts);
        const clubMatch   = scripts.find(s => s.script_key === scriptKey && s.scope === 'club' && s.location_id === clubId);
        const globalMatch = scripts.find(s => s.script_key === scriptKey && s.scope === 'global');
        setScript(clubMatch || globalMatch || null);
      })
      .catch(() => setScript(null))
      .finally(() => setLoading(false));
  }, [pipeline, clubId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div style={s.shell}><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading script…</div></div>
  );

  if (!script) return null;

  const interpolatedBody = interpolateScript(script.body, memberName, repName, clubName);

  function handleCopy() {
    navigator.clipboard.writeText(interpolatedBody).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const methods = Array.isArray(script.contact_method_order) && script.contact_method_order.length > 0
    ? script.contact_method_order : null;

  const pipelineRationale = PIPELINE_WHY[pipelineKey(pipeline)];

  return (
    <div style={s.shell}>
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>📋</span>
          <span style={s.scriptName}>{script.name}</span>
          {script.scope === 'club' && (
            <span style={{ background: 'rgba(234,179,8,0.2)', color: 'var(--yellow)', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>CLUB OVERRIDE</span>
          )}
        </div>
        <button style={s.collapseBtn} onClick={() => setCollapsed(v => !v)}>
          {collapsed ? '▾ Show Script' : '▴ Hide Script'}
        </button>
      </div>

      {!collapsed && (
        <>
          {/* Why This Script */}
          {(script.why_context || pipelineRationale) && (
            <div style={s.whyPanel}>
              <div style={s.whyLabel}>💡 WHY THIS SCRIPT</div>
              <div style={s.whyText}>{script.why_context || pipelineRationale}</div>
            </div>
          )}

          {/* Contact method order with Why? */}
          {methods && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Contact Order</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {methods.map((method, i) => {
                  const isOpen = whyOpen[method];
                  const rationale = CONTACT_METHOD_WHY[method];
                  const icon = method === 'text' ? '💬' : method === 'call' ? '📞' : '📧';
                  const color = method === 'call' ? '#f97316' : method === 'text' ? '#60a5fa' : 'var(--text-muted)';
                  return (
                    <div key={method} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {i > 0 && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>→</span>}
                        <span style={{ background: `${color}22`, color, border: `1px solid ${color}`, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
                          {icon} {method.toUpperCase()}
                        </span>
                        {rationale && (
                          <button
                            style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 4, padding: '1px 6px', fontSize: 9, cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => setWhyOpen(p => ({ ...p, [method]: !p[method] }))}
                          >
                            {isOpen ? 'Less' : 'Why?'}
                          </button>
                        )}
                      </div>
                      {isOpen && rationale && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: 1.5, maxWidth: 340, background: 'var(--surface-3)', padding: '6px 10px', borderRadius: 6 }}>
                          {rationale}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={s.body}>{interpolatedBody}</div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
            <button
              style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 5, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              onClick={handleCopy}
            >
              {copied ? '✓ Copied' : '📋 Copy Script'}
            </button>
            <button
              style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 5, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}
              title="SMS — available in Mass Comms sprint"
            >
              💬 SMS
            </button>
            <button
              style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 5, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}
              onClick={() => setPickerOpen(p => !p)}
            >
              ↕ Switch script
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
              <input type="checkbox" checked={customMode} onChange={e => setCustomMode(e.target.checked)} />
              Used custom script
            </label>
          </div>

          {pickerOpen && (
            <div style={{ marginTop: 8, border: '1px solid #222', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 10px', background: '#1a1a1a', borderBottom: '1px solid #222' }}>
                Lead Engine Scripts — tap to switch
              </div>
              {allScripts.map((s, i) => (
                <button key={s.script_id || i} onClick={() => { setScript(s); setPickerOpen(false); }} style={{
                  width: '100%', textAlign: 'left', padding: '7px 10px',
                  background: s.script_id === script.script_id ? 'rgba(234,179,8,0.1)' : '#111',
                  border: 'none', borderBottom: i < allScripts.length - 1 ? '1px solid #1a1a1a' : 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#e5e5e5' }}>{s.name}</div>
                    {s.timing && <div style={{ fontSize: 10, color: '#666', marginTop: 1 }}>{s.timing}</div>}
                  </div>
                  {s.script_id === script.script_id && (
                    <span style={{ fontSize: 9, fontWeight: 700, color: '#facc15', background: 'rgba(234,179,8,0.15)', padding: '1px 5px', borderRadius: 3 }}>CURRENT</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {customMode && (
            <div style={{ marginTop: 8 }}>
              <textarea
                style={{ width: '100%', background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, padding: '8px', resize: 'vertical', minHeight: 60 }}
                placeholder="Optional: note what you actually said..."
                value={customText}
                onChange={e => setCustomText(e.target.value)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
export default function LeadOutcomeModal({ prospect, onClose, onSaved }) {
  const { post } = useApi();
  const { user }        = useAuth();
  const { currentClub } = useClub();
  const repName  = user?.name || user?.email || '[Rep Name]';
  const clubName = currentClub?.name || '[Club Name]';
  const isDenton = currentClub?.location_id === 'DEN';
  const PLAN_TYPES = isDenton ? DENTON_PLAN_TYPES : BASE_PLAN_TYPES;
  const TIER_SOLD_OPTIONS = isDenton ? TIER_SOLD_OPTIONS_DEN : TIER_SOLD_OPTIONS_BASE;
  const OFFER_TYPES_FULL = isDenton ? OFFER_TYPES_FULL_DEN : OFFER_TYPES_FULL_BASE;
  const [outcome, setOutcome]         = useState('');
  const [planType, setPlanType]       = useState('');
  const [closeResult, setCloseResult] = useState('');
  const [tierSold, setTierSold]       = useState('');
  const [followUp1, setFollowUp1]     = useState('');
  const [followUp2, setFollowUp2]     = useState('');
  const [notes, setNotes]             = useState('');
  const [saving, setSaving]           = useState(false);
  const [contactMethods, setContactMethods] = useState([]);

  function toggleContactMethod(method) {
    setContactMethods(prev =>
      prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
    );
  }
  const [offerSequence, setOfferSequence] = useState({
    offers: [{ made: false, type: '' }, { made: false, type: '' }, { made: false, type: '' }],
    discountUsed: false,
    discountAmount: '',
    promoOffered: false,
    termOffered: '',
    closeDate: '',
  });

  // ─── Sprint 19.7 — Log Note mode ─────────────────────────────────────────
  // Lets a rep log a freeform contact note without picking an outcome or
  // touching any existing follow-up. Writes to outcome_log only — see
  // logContactNote in queries.js. Independent of the outcome flow below.
  const [noteMode,          setNoteMode]          = useState(false);
  const [noteText,          setNoteText]          = useState('');
  const [noteContactMethods, setNoteContactMethods] = useState([]);
  const [noteSaving,        setNoteSaving]        = useState(false);
  const [noteError,         setNoteError]         = useState('');

  function toggleNoteContactMethod(method) {
    setNoteContactMethods(prev =>
      prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
    );
  }

  async function handleSaveNote() {
    setNoteError('');
    if (!noteText.trim()) { setNoteError('Please enter a note.'); return; }
    setNoteSaving(true);
    try {
      await post('/contact/note', {
        module: 'lead',
        refId: prospect.id,
        notes: noteText,
        contactMethods: noteContactMethods,
      });
      onSaved(null);
    } catch (e) {
      setNoteError(e.message || 'Failed to save note.');
    } finally {
      setNoteSaving(false);
    }
  }

  const isSold        = outcome === 'Sold Trial' || outcome === 'Sold Membership';
  const isDNC         = outcome === 'Do Not Contact' || outcome === 'Not Interested — Do Not Contact';
  const showOffers    = OFFER_SEQUENCE_SHOWN_FOR.includes(outcome);
  const showCloseResult = showOffers;
  const showTierSold  = isSold;
  const showFollowUp  = outcome === 'Follow-Up Needed' || outcome === 'Engaged';

  async function handleSave() {
    if (!outcome) return;
    if (isSold && !planType) return;
    setSaving(true);
    try {
      await post('/lead/outcome', {
        prospectId: prospect.id,
        outcome,
        planType: planType || undefined,
        closeResult: closeResult || undefined,
        tierSold: tierSold || undefined,
        followUp1: followUp1 || undefined,
        followUp2: followUp2 || undefined,
        notes,
        contactMethods: contactMethods.length > 0 ? contactMethods : undefined,
        offerSequence: showOffers ? offerSequence : undefined,
      });
      onSaved(outcome);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, width: '100%' }}>
        <div className="modal-title">Log Contact — {prospect.name}</div>

        <div className="card-meta" style={{ marginBottom: '1rem' }}>
          <span>{{
            'lead_engine': 'Lead Engine',
            'win_back_non_member': 'Win Back — Non-Member',
            'win_back_past_member': 'Win Back — Past Member',
            'trial_ready': 'Trial Ready',
          }[prospect.pipeline] || prospect.pipeline}</span>
          {prospect.tier && <span style={{ textTransform: 'capitalize' }}>{prospect.tier}</span>}
          {prospect.phone && <span>{prospect.phone}</span>}
        </div>

        {/* Win Back offer scripts (Sprint 21.3, 9/29/26) — per the "Win Back
            Offer Scoping — TX Clubs" doc. Discount takes priority over the
            90-day offer when both badges would apply (queries.js already
            enforces this — winBackOfferEligible is false whenever
            discountEligible is true — so only one of these ever renders). */}
        {(prospect.discountEligible || prospect.winBackOfferEligible) && (
          <WinBackOfferScript prospect={prospect} clubName={clubName} />
        )}

        {/* Sprint 19.7 — Log Note toggle */}
        <div style={{ marginBottom: 14 }}>
          <button
            onClick={() => setNoteMode(m => !m)}
            style={{
              fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
              border: `1px solid ${noteMode ? '#facc15' : 'var(--border)'}`,
              background: noteMode ? '#facc1515' : 'var(--surface-2)',
              color: noteMode ? '#facc15' : 'var(--text-muted)',
              fontWeight: noteMode ? 700 : 400,
            }}
          >
            📝 {noteMode ? 'Back to Log Outcome' : "Just log a note (keep existing follow-up)"}
          </button>
        </div>

        {noteMode ? (
          <div>
            <div className="field-group">
              <label className="field-label">Contact Method</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
                  { value: 'call',      label: '📞 Call' },
                  { value: 'text',      label: '💬 Text' },
                  { value: 'email',     label: '📧 Email' },
                  { value: 'in-person', label: '🤝 In Person' },
                ].map(cm => {
                  const active = noteContactMethods.includes(cm.value);
                  return (
                    <button
                      key={cm.value}
                      onClick={() => toggleNoteContactMethod(cm.value)}
                      style={{
                        fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                        border: `1px solid ${active ? '#facc15' : 'var(--border)'}`,
                        background: active ? '#facc1515' : 'var(--surface-2)',
                        color: active ? '#facc15' : 'var(--text-muted)',
                        fontWeight: active ? 700 : 400,
                      }}
                    >
                      {cm.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="field-group">
              <label className="field-label">Note *</label>
              <textarea
                className="field-textarea"
                rows={4}
                placeholder="What happened, what did they say..."
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
              />
              <div style={{ fontSize: 10, color: '#555', marginTop: 3 }}>
                Saved to member contact history · does not change status or follow-up
              </div>
            </div>

            {noteError && (
              <div style={{ color: '#ef4444', fontSize: '0.85rem', margin: '0 0 0.5rem', padding: '0.5rem 0.75rem', background: 'rgba(239,68,68,0.1)', borderRadius: '6px' }}>
                {noteError}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveNote} disabled={noteSaving}>
                {noteSaving ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        ) : (
        <>
        <ScriptPanel
          pipeline={prospect.pipeline}
          clubId={prospect.clubId}
          memberName={prospect.name}
          repName={repName}
          clubName={clubName}
        />

        {isDNC && (
          <div style={{ padding: '8px 12px', background: 'rgba(220,38,38,0.1)', border: '1px solid var(--red)', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
            ⚠️ <strong>Hard suppress</strong> — this contact will be permanently removed from ALL pipelines. This cannot be undone.
          </div>
        )}

        <div className="field-group">
          <label className="field-label">Contact Method <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 400 }}>(select all that apply)</span></label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { value: 'call',      label: '📞 Call' },
              { value: 'text',      label: '💬 Text' },
              { value: 'email',     label: '📧 Email' },
              { value: 'in-person', label: '🤝 In Person' },
            ].map(cm => {
              const active = contactMethods.includes(cm.value);
              return (
                <button
                  key={cm.value}
                  onClick={() => toggleContactMethod(cm.value)}
                  style={{
                    fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                    border: `1px solid ${active ? '#facc15' : 'var(--border)'}`,
                    background: active ? '#facc1515' : 'var(--surface-2)',
                    color: active ? '#facc15' : 'var(--text-muted)',
                    fontWeight: active ? 700 : 400,
                  }}
                >
                  {cm.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="field-group">
          <label className="field-label">Outcome *</label>
          <select className="field-select" value={outcome} onChange={e => { setOutcome(e.target.value); setPlanType(''); setCloseResult(''); setTierSold(''); setFollowUp1(''); setFollowUp2(''); }}>
            <option value="">Select outcome...</option>
            {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        {isSold && (
          <div className="field-group">
            <label className="field-label">Plan Type *</label>
            <select className="field-select" value={planType} onChange={e => setPlanType(e.target.value)}>
              <option value="">Select plan...</option>
              {PLAN_TYPES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}

        {showTierSold && (
          <div className="field-group">
            <label className="field-label">Tier Sold</label>
            <select className="field-select" value={tierSold} onChange={e => setTierSold(e.target.value)}>
              <option value="">Select tier...</option>
              {TIER_SOLD_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}

        {showCloseResult && (
          <div className="field-group">
            <label className="field-label">Close Result</label>
            <select className="field-select" value={closeResult} onChange={e => setCloseResult(e.target.value)}>
              <option value="">Select result...</option>
              {CLOSE_RESULTS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}

        {showFollowUp && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div className="field-group" style={{ marginBottom: 0 }}>
              <label className="field-label">Follow-Up 1 Outcome</label>
              <select className="field-select" value={followUp1} onChange={e => setFollowUp1(e.target.value)}>
                <option value="">Select...</option>
                {FOLLOWUP_OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="field-group" style={{ marginBottom: 0 }}>
              <label className="field-label">Follow-Up 2 Outcome</label>
              <select className="field-select" value={followUp2} onChange={e => setFollowUp2(e.target.value)}>
                <option value="">Select...</option>
                {FOLLOWUP_OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
        )}

        {showOffers && (
          <OfferSequencePanel
            offerSequence={offerSequence}
            onChange={setOfferSequence}
          />
        )}

        <div className="field-group">
          <label className="field-label">Notes</label>
          <textarea className="field-textarea" placeholder="Add notes (saved with outcome)..." value={notes} onChange={e => setNotes(e.target.value)} />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            style={isDNC ? { background: 'var(--red)', borderColor: 'var(--red)' } : {}}
            onClick={handleSave}
            disabled={!outcome || (isSold && !planType) || saving}
          >
            {saving ? 'Saving...' : isDNC ? 'Confirm DNC' : 'Save'}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

const s = {
  shell: { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  scriptName: { fontSize: 13, fontWeight: 600, color: 'var(--text)' },
  collapseBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, padding: 0 },
  whyPanel: { background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, padding: '8px 12px', marginBottom: 10 },
  whyLabel: { fontSize: 9, fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 },
  whyText: { fontSize: 12, color: 'var(--text)', lineHeight: 1.5 },
  body: { fontSize: 13, color: 'var(--text)', lineHeight: 1.6, padding: '8px 12px', background: 'var(--surface-3)', borderRadius: 6, marginBottom: 8, whiteSpace: 'pre-wrap' },
};
